import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	ParentBoundComponent,
	Module,
	Button,
	Icon,
	Form,
	FormItem,
	LookupInput,
	Cross,
	DateInput,
	TextInput,
	webapi,
	REPORTED_EXCEPTION,
	isEmpty,
	DateUtils,
	FlexBox,
	FlexBoxBlock,
} from 'ps-gui';

import {
	getColumnText,
	getPhrase,
} from '../../data/phrases';
import LabelModel from './labelmodel';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock,
} from '../../blocks/modulesections';
import { ReactComponent as ModuleLogo } from '../../svg/labels.svg';
import {
	exportToExcel,
	formatHtml,
	getGlobalContextChangeChecker
} from '../utils';
import {
	Dialog,
	DialogButtons,
	DialogContent,
	DialogHeader,
	DialogSubtext,
} from '../../components/dialog';
import { withGlobalContext } from '../../data/globalcontext';
import { CellMainText, CellSubText } from '../portalresultsetmodule';
import Message from '../../blocks/message';
import { getLockMessage } from './labelutils';
import { getLanguageId } from '../../data/session';


const Wrapper = styled.div`
`;

const Content = styled.div`
	margin-top: 24px;
`;

const FormWrapper = styled.div`
	max-width: 700px;
	.ps-formItem {
		margin-bottom: 24px;
	}
`;

const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const TabText = styled.span`

	.icon {
		position: relative;
		top: -1px;
	}

	.icon.left {
		margin-right: 8px;
	}

	.icon[data-name=close] {
		margin-left: 16px;
		* {
			fill: #4FC098;
		}
	}
`;

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const DialogLabel = styled.div`
	position: relative;
	.createdWhen {
		color: #575757CC;
		position: absolute;
		right: 0px;
		top: 0px;
		font-weight: 300;
		font-size: 14px;
		line-height: 30px;
	}

	.type {
		padding-right: 80px;
		font-weight: 400;
		font-size: 16px;
		line-height: 24px;
	}

	.name {
		font-weight: 500;
		font-size: 16px;
		line-height: 30px;
		color: #575757;
		margin-right: 16px;
		display: inline-block;
	}
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);


const recAttrs = [
	'isrc',
	'title',
	'subtitle',
	'producer',
	'label',
	'recordingTypeId',
	'genreId',
	'productionYear',
	'releaseYear',
	'studio',
	'masterNumber',
	'albumCatalogNumber',
	'trackNumber',
	'length',
	'language',
	'recordingRegistrationDate',
	'recordingCountryId',
	'album',
	'mainArtist',
];

const RecordingAttribute = styled.div`
	padding: 2px 8px;
	white-space: nowrap;

	.label {
		display: inline-block;
		min-width: 220px;
		padding-right: 8px;
		color: ${props => props.theme.disabledTextColor};
	}

	.value {
		display: inline-block;
		font-weight: 500;
		padding-right: 8px;
		min-width: 220px;
		white-space: normal;
		max-width: calc( 100vw - 440px );
	}

	.subvalue {
		display: inline-block;
		font-size: 90%;
		margin-left: 8px;
	}

	cursor: default;
	&:hover {
		background: ${props => props.theme.tableRowSelectedBackground};
	}
`;

const SubHeader = styled.div`
	text-align: center;
	margin: 8px 0px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
`;

class LabelModule extends ParentBoundComponent {

	constructor() {
		super();

		this.customResultProps = Object.assign({}, customResultProps);
		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
		this.onRowAction = this.onRowAction.bind(this);
		this.openDetail = this.openDetail.bind(this);
		this.setPartyId = this.setPartyId.bind(this);
		this.saveDetail = this.saveDetail.bind(this);
		this.newLabel = this.newLabel.bind(this);
		this.customResultProps.toolBar =
			<Icon
				name="refresh"
				active
				hint={getPhrase('refresh')}
				onMouseDown={async () => {
					this.setModel(await this.getModel().refreshSearch(true));
				}}
			/>;
		this.customResultProps.renderIdNotFoundMessage = this.renderIdNotFoundMessage;
		this.exportToExcel = this.exportToExcel.bind(this);
	}

	exportToExcel() {
		const data = this.getData();
		exportToExcel('API_LabelExcelExport', {
			producerId: data.get('partyId'),
			langId: getLanguageId()
		}, 'label.xlsx');
	}

	renderIdNotFoundMessage(errorCode) {
		return (
			<>
				<CellMainText className="ok">{getPhrase('labelCreatedSuccessfully')}</CellMainText>
				<CellSubText>{getPhrase('labelIdNotFoundSubText')}</CellSubText>
			</>
		);
	}

	async openDetail(producerId, labelOwnershipId) {
		this.setModel(await this.getModel().openDetail(producerId, labelOwnershipId));
	}

	async onRowAction(row, rowIndex, options) {
		this.openDetail(row.get('producer_id'), row.get('label_ownership_id'));
	}

	newLabel() {
		this.setModel(this.getModel().setDetail(Immutable.Map({
			isDeleted: 'N',
			fromDate: null,
			toDate: null,
			labelName: '',
			labelId: null,
			producerId: this.getData().get('partyId')
		})));
	}

	async saveDetail() {
		let detail = this.getData().get('detail');
		if (!detail) {
			return;
		}
		let originalDetail = this.getData().get('originalDetail');
		if (isEmpty(detail.get('labelName'))) {
			this.setDetail(detail.set('labelNameError', getPhrase('labelNameIsMandatory')));
			return;
		}
		if (!this.validateDetailDates()) {
			return;
		}
		if (detail !== originalDetail) {
			let pars = detail.toJS();
			const res = await webapi.execute('/api/proc/api_labels/saveLabel', pars);
			if (res === REPORTED_EXCEPTION) {
				return;
			}
			let model = this.getModel();
			let srch = model.getSearch();
			if (!pars.id && res.id) {
				srch = srch.selectIdOnNextSearch(res.id, 'newLabelNotInResultSet');
			}
			srch = await srch.search(undefined, true);
			this.setModel(model.hideDetail().setSearch(srch));
		} else {
			this.setModel(this.getModel().hideDetail());
		}
	}

	getModel() {
		return new LabelModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	async componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	async setPartyId(value) {
		this.setModel(await this.getModel().setPartyId(value));
	}

	/*
					<RowCountEtc>
						<RowCount>
							<span className="label">{getPhrase('recordingCount')}:</span>
							<span className="value">{(data.getIn([
								'search',
								'data',
								'result',
								'data',
								'pager',
								'totalCount'
							]))}</span>
						</RowCount>
					</RowCountEtc>

	*/
	renderList() {
		const data = this.getData();
		const isLocked = data.get('isLocked');
		return (
			<Content>
				<Module
					appState={data.get('search')}
					onDataChange={newData => {
						this.setData(data.setIn(['search', 'data'], newData));
					}}
					customResultProps={this.customResultProps}
					onRowAction={isLocked ? null : this.onRowAction}
					key={data.get('listKey') || 0}
				//leftToolBar={this.renderCandidateListToolbar}
				//onResultIconMouseDown={this.onCandidateIconMouseDown}
				/>
			</Content>
		);
	}

	setDetail(detail) {
		this.setModel(this.getModel().setDetail(detail));
	}

	validateDetailDates() {
		const data = this.getData();
		let detail = data.get('detail');
		if (!detail) {
			return false;
		}
		// const startOfYear = data.get('startOfYear');
		// const endOfYear = data.get('endOfYear');
		const fromDate = detail.get('fromDate');
		const toDate = detail.get('toDate');
		let fromDateError = null;
		let toDateError = null;
		/*if (fromDate && (DateUtils.compareDates(fromDate, endOfYear) > 0 || DateUtils.compareDates(fromDate, startOfYear) < 0)) {
			fromDateError = getPhrase('dateIsOutsideYear').replace('$year', data.get('year'));
		}
		if (toDate && (DateUtils.compareDates(toDate, endOfYear) > 0 || DateUtils.compareDates(toDate, startOfYear) < 0)) {
			toDateError = getPhrase('dateIsOutsideYear').replace('$year', data.get('year'));
		}*/
		if (isEmpty(fromDate)) {
			fromDateError = getPhrase('fromDateIsMandatory');
		}
		if (fromDate && toDate && !toDateError && DateUtils.compareDates(fromDate, toDate) > 0) {
			toDateError = getPhrase('toDateIsLessThanFromDate');
		}
		detail = detail.set('fromDateError', fromDateError).set('toDateError', toDateError);
		this.setDetail(detail);
		return !(fromDateError || toDateError);
	}

	renderDetail() {
		const data = this.getData();
		let detail = data.get('detail');

		if (!detail) {
			return null;
		}


		const year = data.get('year');
		// const labelId = detail.get('labelId');
		const isDeleted = detail.get('isDeleted') !== 'N';
		const labelIsDeletedMessage = getPhrase('labelIsDeletedMessage').replace('$year', year);
		return (
			<Dialog
				onCloseRequested={() => {
					this.setModel(this.getModel().hideDetail());
				}}
			>
				<DialogHeader marginBottom="16px">{detail.get('labelOwnershipId') ? getPhrase('labelDetail') : getPhrase('newLabel')}</DialogHeader>
				<DialogSubtext maxWidth="640px" marginBottom="16px">
					{
						/*labelId ? getPhrase('labelDetailSubheader').replace('$year', year).replace('$labelIsDeleted', labelIsDeletedMessage) : getPhrase('newLabelDetailSubheader')*/
					}
				</DialogSubtext>
				<DialogContent maxHeight="calc( 100vh - 200px )" overflow="auto">
					<Form onAction={this.saveDetail}>
						<FormItem label={getColumnText('labelName')} error={detail.get('labelNameError')}>
							<TextInput
								value={detail.get('labelName')}
								changeOnBlur
								onChange={value => {
									if (isEmpty(value)) {
										detail = detail.set('labelNameError', getPhrase('labelNameIsMandatory'));
									} else {
										detail = detail.delete('labelNameError');
									}
									this.setDetail(detail.set('labelName', value));
								}}
								readOnly={isDeleted}
							/>
						</FormItem>
						<Cross>
							<FormItem label={getColumnText('fromDate')} error={detail.get('fromDateError')}>
								<DateInput
									value={detail.get('fromDate')}
									// placeholder={data.get('startOfYearStr')}
									onChange={value => {
										this.setDetail(detail.set('fromDate', value));
										this.validateDetailDates();
									}}
									readOnly={isDeleted}
								/>
							</FormItem>
							<FormItem label={getColumnText('toDate')} error={detail.get('toDateError')}>
								<DateInput
									value={detail.get('toDate')}
									// placeholder={data.get('endOfYearStr')}
									onChange={value => {
										this.setDetail(detail.set('toDate', value));
										this.validateDetailDates();
									}}
									readOnly={isDeleted}
								/>
							</FormItem>
						</Cross>
						{
							/*labelId ? (
								<FormItem>
									<CheckBoxContainer onMouseDown={e => {
										e.preventDefault();
										this.setDetail(detail.set('isDeleted', isDeleted ? 'N' : 'Y'));
									}}>
										<Icon
											name={isDeleted ? 'checkBoxChecked' : 'checkBoxUnchecked'}
										/>
										<span className='text'>{labelIsDeletedMessage}</span>
									</CheckBoxContainer>
								</FormItem>
							) : null*/
						}
					</Form>
				</DialogContent>
				<DialogButtons className="right">
					<Button
						text={getPhrase('close')}
						type="medium"
						onAction={() => {
							this.setModel(this.getModel().hideDetail());
						}}
					/>
					<Button
						text={getPhrase('save')}
						onAction={this.saveDetail}
					/>
				</DialogButtons>
			</Dialog>
		);
	}

	renderLockedMessage(lockedFromDate, lockedToDate) {
		if (!lockedFromDate) {
			return null;
		}
		const message = getLockMessage(lockedFromDate, lockedToDate);
		if (message) {
			return (
				<Message
				>
					<span dangerouslySetInnerHTML={{ __html: formatHtml(message) }} />
				</Message>
			);
		}
		return null;
	}

	render() {
		const data = this.getData();
		const partyLookup = data.get('partyLookup');
		if (!partyLookup) {
			return null;
		}
		const languageTimestamp = data.get('languageTimestamp') || 0;
		return (
			<Wrapper>
				<ModuleHeaderSection padding="16px 0px">
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('Label')/*getPhrase('registeredLabelsForYear').replace('$year', data.get('year'))*/}</ModuleHeader>
						<Form virtual horizontal>
							<FormItem label={getColumnText('producer')} fixedWidth="470px" marginRight="30px">
								<LookupInput
									getText={partyLookup.getText}
									getRows={partyLookup.getRows}
									combo
									mandatory
									value={data.get('partyId')}
									onChange={this.setPartyId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
						</Form>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock padding="0px">
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				<ModuleBodySection>
					{this.renderLockedMessage(data.get('lockedFromDate'), data.get('lockedToDate'))}
					<RowCountEtc>
						<FlexBox>
							<FlexBoxBlock flexGrow="1">
								<RowCount>
									<span className="label">{getPhrase('labelCount')}:</span>
									<span className="value">{data.getIn([
										'search',
										'data',
										'result',
										'data',
										'pager',
										'totalCount'
									])}</span>
								</RowCount>
								{
									!data.get('isLocked') && (
										<Button
											type="medium"
											text={getPhrase('newLabel')}
											onAction={this.newLabel}
										/>
									)
								}
							</FlexBoxBlock>
							<FlexBoxBlock>
								<Button
									type="medium"
									text={getPhrase('exportLabelsToExcel')}
									onAction={this.exportToExcel}
								/>
							</FlexBoxBlock>
						</FlexBox>
					</RowCountEtc>
					{this.renderList()}
					{this.renderDetail()}
				</ModuleBodySection>
			</Wrapper>
		);
	}
}


const cellRendererMap = Object.freeze({
	'label_name': (cell, row) => {
		return <CellMainText>{cell}</CellMainText>;
	},
});

const CheckBoxContainer = styled.div`
	display: table;
	margin: 4px auto;
	cursor: pointer;
	text-align: center;
	.icon {
		margin-right: 4px;
		position: relative;
		top: -2px;
	}

	&:hover {
		text-decoration: underline;
	}
`;

const customResultProps = {
	cellRendererMap,
	withRowSelection: true,
	rowCountLabel: () => getPhrase('labelCount'),
	defaultColumnWidths: {
		from_date: '120px',
		to_date: '120px',
		modified: '120px',
		newly_added: '120px',
		is_deleted: '120px'
	},
	columnAlign: {
	},
	columnFilters: {
		label_name: {
			type: 'text'
		},
		from_date: {
			type: 'date'
		},
		to_date: {
			type: 'date'
		},
		newly_added: {
			type: 'lookup',
			lookup: 'ynBoolean'
		},
		is_deleted: {
			type: 'lookup',
			lookup: 'ynBoolean'
		},
		modified: {
			type: 'lookup',
			lookup: 'ynBoolean'
		},
	}
};

export default withGlobalContext(LabelModule);
